import { useCallback, useEffect, useMemo, useState } from 'react';
import { IoSearchSharp } from 'react-icons/io5';
import HOC from '../../components/HOC/HOC';
import Pagination from '../../components/Pagination/Pagination';
import styles from '../../css/style.module.css';
import img from '../../assest/loading1.gif';
import { getApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { formatDateTimeP } from '../../utils/utils';
import { formatCurrency } from '../Wallet Management/staffWalletDateUtils';
import pageStyles from './StaffPenalty.module.css';

const CODE_OPTIONS = [
  { label: 'All Codes', value: '' },
  { label: 'Grace', value: 'GRACE' },
  { label: 'After Grace', value: 'AFTER_GRACE' },
  { label: 'Not Started', value: 'NOT_STARTED' },
];

const DEDUCTED_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Deducted', value: 'true' },
  { label: 'Skipped', value: 'false' },
];

const CODE_LABELS = {
  GRACE: 'Grace',
  AFTER_GRACE: 'After Grace',
  NOT_STARTED: 'Not Started',
};

const CODE_BADGE_CLASS = {
  GRACE: 'codeGrace',
  AFTER_GRACE: 'codeAfterGrace',
  NOT_STARTED: 'codeNotStarted',
};

const formatUtcTime = value => {
  if (!value) return '-';
  return formatDateTimeP(value) || '-';
};

const formatScheduledStart = item => {
  const utc = item?.validation?.scheduledStartUtc;
  if (utc) return formatUtcTime(utc);

  if (!item?.fromTime) return '-';
  const date = new Date(item.fromTime);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatStartedAt = item => {
  const startedAt = item?.jobStartedAt || item?.validation?.jobStartedAtUtc;
  if (!startedAt) return 'Not started';
  return formatUtcTime(startedAt);
};

const formatLateBy = item => {
  const seconds = item?.lateSeconds ?? item?.validation?.lateSeconds;
  if (seconds == null) return '-';

  const abs = Math.abs(seconds);
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const remainingSeconds = abs % 60;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!hours && remainingSeconds) parts.push(`${remainingSeconds}s`);
  if (!parts.length) parts.push('0m');

  const duration = parts.join(' ');
  if (seconds < 0) return `Early ${duration}`;
  return duration;
};

const StaffPenalty = () => {
  const [penaltyData, setPenaltyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [code, setCode] = useState('');
  const [deducted, setDeducted] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');
  const [pagination, setPagination] = useState({
    limit: 10,
    totalPages: 1,
    page: 1,
    hasPrevPage: false,
    hasNextPage: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(prev => {
        if (prev === search) return prev;
        setPagination(p => (p.page === 1 ? p : { ...p, page: 1 }));
        return search;
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    await getApi(
      endPoints.getStaffPenalties({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch,
        code,
        deducted,
        fromDate: appliedFromDate,
        toDate: appliedToDate,
      }),
      {
        setResponse: setPenaltyData,
        setLoading,
        errorMsg: 'Failed to fetch staff penalties!',
        onError: () => setPenaltyData(null),
      }
    );
  }, [
    pagination.page,
    pagination.limit,
    debouncedSearch,
    code,
    deducted,
    appliedFromDate,
    appliedToDate,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!penaltyData?.data) return;

    const totalDocs = penaltyData.data.totalDocs ?? 0;
    const page = penaltyData.data.page ?? 1;
    const limit = penaltyData.data.limit ?? 10;
    const totalPages = Math.max(1, Math.ceil(totalDocs / limit) || 1);

    setPagination(prev => ({
      ...prev,
      totalPages,
      hasPrevPage: page > 1,
      hasNextPage: page < totalPages,
    }));
  }, [penaltyData]);

  const handleFilterChange = setter => event => {
    setter(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleApplyDates = () => {
    if (!fromDate || !toDate) return;
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setCode('');
    setDeducted('');
    setFromDate('');
    setToDate('');
    setAppliedFromDate('');
    setAppliedToDate('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const summary = penaltyData?.summary;
  const penalties = penaltyData?.data?.docs ?? [];
  const totalRecords = penaltyData?.data?.totalDocs ?? 0;
  const hasActiveFilters = Boolean(
    search || code || deducted || appliedFromDate || appliedToDate
  );

  const summaryCards = useMemo(
    () => [
      { label: 'Total', value: summary?.totalPenalties ?? 0 },
      { label: 'Deducted', value: summary?.deductedCount ?? 0 },
      { label: 'Skipped', value: summary?.skippedCount ?? 0 },
      { label: 'Deducted Amount', value: formatCurrency(summary?.totalDeductedAmount) },
    ],
    [summary]
  );

  return (
    <div className={`${styles.jobmanagementCotainer} ${pageStyles.page}`}>
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <h6>Staff Penalty</h6>
          <p>Audit late-start penalties against scheduled job time</p>
        </div>
        <div className={pageStyles.searchBar}>
          <IoSearchSharp color="#8A8A8A" size={18} />
          <input
            type="search"
            placeholder="Search staff, phone, email, or employee ID"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={pageStyles.summaryGrid}>
        {summaryCards.map(card => (
          <div key={card.label} className={pageStyles.summaryCard}>
            <p className={pageStyles.summaryCardLabel}>{card.label}</p>
            <p className={pageStyles.summaryCardValue}>{card.value}</p>
          </div>
        ))}
      </div>

      <div className={pageStyles.filterPanel}>
        <div className={pageStyles.typeFilter}>
          <label htmlFor="staff-penalty-code">Penalty Code</label>
          <select id="staff-penalty-code" value={code} onChange={handleFilterChange(setCode)}>
            {CODE_OPTIONS.map(option => (
              <option key={option.value || 'all-codes'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={pageStyles.typeFilter}>
          <label htmlFor="staff-penalty-deducted">Deduction</label>
          <select
            id="staff-penalty-deducted"
            value={deducted}
            onChange={handleFilterChange(setDeducted)}
          >
            {DEDUCTED_OPTIONS.map(option => (
              <option key={option.value || 'all-deducted'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={pageStyles.typeFilter}>
          <label htmlFor="staff-penalty-from-date">From Date</label>
          <input
            id="staff-penalty-from-date"
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
        </div>
        <div className={pageStyles.typeFilter}>
          <label htmlFor="staff-penalty-to-date">To Date</label>
          <input
            id="staff-penalty-to-date"
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            min={fromDate || undefined}
          />
        </div>
        <button
          type="button"
          className={pageStyles.applyButton}
          onClick={handleApplyDates}
          disabled={!fromDate || !toDate}
        >
          Apply Dates
        </button>
        <button
          type="button"
          className={pageStyles.clearButton}
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear
        </button>
      </div>

      <div className={pageStyles.tableSection}>
        <div className={pageStyles.tableSectionHeader}>
          <h6>Penalty Audit</h6>
          <span className={pageStyles.recordCount}>{totalRecords} records</span>
        </div>

        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Staff</th>
                <th>Site</th>
                <th>Scheduled Start</th>
                <th>Started At</th>
                <th>Late By</th>
                <th>Code</th>
                <th className={pageStyles.alignRight}>Amount</th>
                <th>Deducted</th>
                <th>Validation</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className={pageStyles.loadingCell}>
                    <img src={img} alt="Loading..." width={80} height={80} />
                  </td>
                </tr>
              ) : penalties.length ? (
                penalties.map((item, index) => {
                  const penalty = item?.penalty || {};
                  const validation = item?.validation || {};
                  const issues = Array.isArray(validation.issues) ? validation.issues : [];
                  const codeClass = CODE_BADGE_CLASS[penalty.code] || '';

                  return (
                    <tr key={item?.staffBookingId || item?.sNo || index}>
                      <td>{item?.sNo ?? (pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td>
                        <div className={pageStyles.staffCell}>
                          <span className={pageStyles.staffName}>{item?.staff?.fullName || '-'}</span>
                          {item?.staff?.Id && (
                            <span className={pageStyles.staffMeta}>{item.staff.Id}</span>
                          )}
                        </div>
                      </td>
                      <td>{item?.siteName || '-'}</td>
                      <td>{formatScheduledStart(item)}</td>
                      <td>{formatStartedAt(item)}</td>
                      <td>
                        <span className={pageStyles.lateBy}>{formatLateBy(item)}</span>
                      </td>
                      <td>
                        <span className={`${pageStyles.codeBadge} ${pageStyles[codeClass] || ''}`}>
                          {CODE_LABELS[penalty.code] || penalty.code || '-'}
                        </span>
                      </td>
                      <td className={`${pageStyles.alignRight} ${pageStyles.debitAmount}`}>
                        {formatCurrency(penalty.amount)}
                      </td>
                      <td>
                        <div className={pageStyles.staffCell}>
                          <span
                            className={`${pageStyles.statusBadge} ${
                              penalty.deducted ? pageStyles.deductedYes : pageStyles.deductedNo
                            }`}
                          >
                            {penalty.deducted ? 'Yes' : 'No'}
                          </span>
                          {!penalty.deducted && penalty.skipReason && (
                            <span className={pageStyles.staffMeta}>{penalty.skipReason}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={pageStyles.validationCell}>
                          <span
                            className={`${pageStyles.statusBadge} ${
                              validation.matchesExpected
                                ? pageStyles.validationMatch
                                : pageStyles.validationMismatch
                            }`}
                          >
                            {validation.matchesExpected ? 'Match' : 'Mismatch'}
                          </span>
                          {!validation.matchesExpected && (
                            <span className={pageStyles.staffMeta}>
                              Expected {CODE_LABELS[validation.expectedCode] || validation.expectedCode}{' '}
                              {formatCurrency(validation.expectedAmount)}
                            </span>
                          )}
                          {issues[0] && <span className={pageStyles.issueText}>{issues[0]}</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className={pageStyles.emptyCell}>
                    <div className={pageStyles.emptyState}>
                      <p>No staff penalties found</p>
                      <span>Try adjusting search or filters</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={pageStyles.paginationWrap}>
          <Pagination
            limit={pagination.limit}
            totalPages={pagination.totalPages}
            page={pagination.page}
            hasPrevPage={pagination.hasPrevPage}
            hasNextPage={pagination.hasNextPage}
            onPageChange={newPage => setPagination(prev => ({ ...prev, page: newPage }))}
          />
        </div>
      </div>
    </div>
  );
};

export default HOC(StaffPenalty);
